import { NetworkProps, enabledNetworks } from '@dapp/multi-lib/network';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { LabelSmall } from 'baseui/typography';
import { cx } from 'class-variance-authority';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import styles from '../bridge.module.css';
import Image from 'next/image';
import { useAtom } from 'jotai';
import { bridgeAtom } from 'structure/bridge/store/bridgeAtom';
import { getStaticAssetUrl } from 'utils/url';
import {
  BridgeNetworkList,
  BridgeNetworkOption,
} from '../BridgeNetworkList/BridgeNetworkList';

type INetworks = BridgeNetworkOption;

export const BridgeNetworkSelection: React.FC<{
  allowedNetworks?: number[];
  embedded?: boolean;
}> = ({ allowedNetworks, embedded = false }) => {
  const [networks, setNetworks] = useState<INetworks[]>([]);
  const [bridgeState, setBridgeState] = useAtom(bridgeAtom);
  const [selectNetworkOpen, setSelectNetworkOpen] = useState(false);
  const { t } = useTranslation('migrate');
  const toastV2 = useToast();

  useEffect(() => {
    const supportedNetworks = enabledNetworks
      .filter((item) => {
        if (allowedNetworks && allowedNetworks.length > 0) {
          return allowedNetworks.includes(item.id);
        }
        return false;
      })
      .map((n) => {
        return {
          id: n.id,
          network: n,
          subtitle: n.name,
          imgUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/${n.shortName}.png`,
        };
      });

    setNetworks(supportedNetworks);
  }, [allowedNetworks]);

  const selectedNetwork = useMemo(
    () =>
      networks.find((network) => network.id === bridgeState.dstChainId) ??
      networks[0],
    [bridgeState.dstChainId, networks]
  );

  useEffect(() => {
    if (networks.length === 0) return;

    setBridgeState((state) =>
      networks.some((network) => network.id === state.dstChainId)
        ? state
        : { ...state, dstChainId: networks[0].id }
    );
  }, [networks, setBridgeState]);

  const handleNetworkChange = (chain: NetworkProps) => {
    try {
      setSelectNetworkOpen(false);
      setBridgeState((state) => ({ ...state, dstChainId: chain.id }));
    } catch {
      toastV2({
        title: t('common:ERROR_MESSAGES.ERROR'),
        description: t('common:NETWORK_CHANGE_ERROR'),
        type: 'error',
      });
    }
  };

  const networkSelector = (
    <button
      type='button'
      aria-expanded={selectNetworkOpen}
      className={cx(
        'cursor-pointer text-white',
        embedded
          ? 'flex h-full min-w-0 max-w-[132px] items-center gap-2 px-2 sm:max-w-[210px] sm:px-4'
          : 'w-full'
      )}
      onClick={() => setSelectNetworkOpen((open) => !open)}
    >
      <div
        className={cx(
          'flex min-w-0 items-center text-white',
          embedded
            ? 'gap-2'
            : 'h-[42px] w-full justify-between rounded-[20px] bg-[#191A1E] px-4'
        )}
      >
        <div
          className={cx('flex min-w-0 items-center gap-2', {
            'ml-auto': !embedded,
          })}
        >
          <Image
            src={selectedNetwork?.imgUrl ?? ''}
            className='shrink-0 rounded-full'
            alt='TokenIcon'
            width={24}
            height={24}
          />
          <LabelSmall
            className={cx(
              twMerge(
                styles.bridgeFont,
                embedded && styles.responsiveLabel,
                'truncate text-lg'
              )
            )}
          >
            {selectedNetwork?.network.name}
          </LabelSmall>
        </div>
        <div className={cx('shrink-0', { 'ml-2 mr-1': !embedded })}>
          <Image
            src={getStaticAssetUrl('/sfi/static/svgs/arrow-down.svg')}
            width={16}
            height={16}
            alt='arrow-down'
          />
        </div>
      </div>
    </button>
  );

  if (embedded) {
    return (
      <>
        {networkSelector}
        <BridgeNetworkList
          open={selectNetworkOpen}
          options={networks}
          fullWidthOverlay
          onClickOutside={() => setSelectNetworkOpen(false)}
          onChange={(option) => handleNetworkChange(option.network)}
        />
      </>
    );
  }

  return (
    <>
      <LabelSmall
        className={cx(twMerge(styles.label, 'mb-2 text-sm text-white-600'))}
      >
        {t('NETWORK')}
      </LabelSmall>
      {networkSelector}
      <BridgeNetworkList
        open={selectNetworkOpen}
        options={networks}
        onClickOutside={() => setSelectNetworkOpen(false)}
        onChange={(option) => handleNetworkChange(option.network)}
      />
    </>
  );
};
